import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiCalendar, FiCreditCard } from 'react-icons/fi';
import { CustomerDetail } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import { formatDateTimeKST } from '../utils/dateUtils';
import PaymentHistoryTableMobile from '../components/PaymentHistoryTableMobile';
import KeywordSettingTableMobile from '../components/KeywordSettingTableMobile';
import PointHistoryTableMobile from '../components/PointHistoryTableMobile';
import CustomerIpAllowlistTab from '../components/CustomerIpAllowlistTab';
import { colors, borderRadius, shadows, zIndex } from '../styles/designTokens';

// 모바일 페이지 컨테이너 - 밀도 있는 디자인
const MobilePageContainer = styled.div`
  min-height: 100vh;
  background-color: ${colors.semantic.background.secondary};
  padding-bottom: 100px; /* 하단 네비게이션 공간 확보 */
`;

// 모바일 헤더 - 컴팩트
const MobileHeader = styled.div`
  background-color: ${colors.semantic.background.primary};
  padding: 12px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
  position: sticky;
  top: 0;
  z-index: ${zIndex.docked};
`;

// 모바일 헤더 내용
const MobileHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 뒤로가기 버튼 - 작게
const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: ${colors.neutral[100]};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.neutral[200]};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// 모바일 헤더 제목 - 작게
const MobileHeaderTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
  line-height: 1.2;
`;

// 모바일 콘텐츠 영역 - 밀도 있게
const MobileContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// 모바일 프로필 카드
const MobileProfileCard = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 16px;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
  text-align: center;
`;

// 모바일 아바타
const MobileAvatar = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${colors.warning[400]} 0%, ${colors.warning[500]} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
  font-size: 32px;
  
  &::after {
    content: '🌱';
  }
`;

// 모바일 고객 ID
const MobileCustomerId = styled.p`
  font-size: 14px;
  color: ${colors.semantic.text.tertiary};
  margin: 0 0 16px 0;
`;

// 모바일 통계 그리드
const MobileStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
`;

// 모바일 통계 카드
const MobileStatsCard = styled.div`
  background-color: ${colors.semantic.background.secondary};
  border-radius: ${borderRadius.lg};
  padding: 12px;
  text-align: center;
  border: 1px solid ${colors.semantic.border.primary};

  .value {
    font-size: 16px;
    font-weight: 700;
    color: ${colors.secondary[800]};
    margin-bottom: 4px;
  }

  .label {
    font-size: 11px;
    color: ${colors.semantic.text.tertiary};
    font-weight: 500;
  }
`;

// 모바일 정보 카드
const MobileInfoCard = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 14px;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 카드 제목
const MobileCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.secondary[100]};
`;

// 모바일 정보 행
const MobileInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.semantic.background.secondary};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

// 모바일 정보 라벨
const MobileInfoLabel = styled.span`
  font-size: 14px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  .icon {
    font-size: 16px;
    color: ${colors.semantic.text.disabled};
  }
`;

// 모바일 정보 값
const MobileInfoValue = styled.span`
  font-size: 14px;
  color: ${colors.secondary[800]};
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  word-break: break-all;
`;

const MobileTextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.secondary[800]};
  background: ${colors.semantic.background.primary};

  &:focus {
    outline: none;
    border-color: ${colors.primary[400]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const MobileColumnRow = styled(MobileInfoRow)`
  flex-direction: column;
  align-items: stretch;
`;

const MobileSaveButton = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.semantic.text.inverse};
  background: ${colors.primary[500]};
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.primary[600]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MobileProfileFlash = styled.div<{ $error?: boolean }>`
  margin-top: 10px;
  font-size: 13px;
  color: ${props => (props.$error ? colors.error[600] : colors.success[700])};
`;

// 모바일 로딩 스피너
const MobileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${colors.semantic.border.primary};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 에러 메시지
const MobileErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: 16px;
  border-radius: ${borderRadius.lg};
  text-align: center;
  font-size: 14px;
  margin: 16px;
`;

// 모바일 탭 컨테이너
const MobileTabContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.neutral[200]};
  box-shadow: ${shadows.base};
  margin-top: 8px;
  overflow: hidden;
`;

// 모바일 탭 헤더
const MobileTabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.semantic.background.secondary};
  position: sticky;
  top: 56px;
  z-index: ${zIndex.docked};
`;

// 모바일 탭 버튼
const MobileTabButton = styled.button<{ active: boolean }>`
  flex: 1;
  min-height: 42px;
  padding: 10px 8px;
  border: none;
  background: ${props =>
    props.active ? colors.semantic.background.primary : 'transparent'};
  color: ${props => (props.active ? colors.secondary[800] : colors.semantic.text.tertiary)};
  font-size: 13px;
  font-weight: ${props => (props.active ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: ${props => (props.active ? `2px solid ${colors.primary[500]}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  &:active {
    opacity: 0.92;
  }
`;

// 모바일 탭 아이콘
const MobileTabIcon = styled.span`
  font-size: 16px;
`;

// 모바일 탭 콘텐츠
const MobileTabContent = styled.div`
  padding: 10px;
  min-height: auto;
  background: ${colors.semantic.background.primary};
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  th,
  td {
    padding: 8px;
    white-space: nowrap;
  }

  th {
    background-color: ${colors.semantic.background.secondary};
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    text-align: left;
    border-bottom: 1px solid ${colors.semantic.border.primary};
  }

  td {
    border-bottom: 1px solid ${colors.secondary[100]};
    color: ${colors.secondary[800]};
  }

  td:nth-child(2) {
    text-align: right;
  }

  td:nth-child(3) {
    text-align: center;
  }

  .status {
    padding: 4px 8px;
    border-radius: ${borderRadius.base};
    font-size: 11px;
    font-weight: 500;
  }

  .status-success {
    background-color: ${colors.success[100]};
    color: ${colors.success[800]};
  }

  .status-pending {
    background-color: ${colors.warning[100]};
    color: ${colors.warning[800]};
  }

  .status-failed {
    background-color: ${colors.error[100]};
    color: ${colors.error[800]};
  }
`;


// 탭 타입 정의
type TabType = 'payments' | 'keywords' | 'points' | 'ip';

// 탭 정보 인터페이스
interface TabInfo {
  id: TabType;
  label: string;
  icon: string;
}

// 탭 목록
const tabs: TabInfo[] = [
  { id: 'payments', label: '결제 내역', icon: '💰' },
  { id: 'keywords', label: '키워드 설정', icon: '🏷️' },
  { id: 'points', label: '포인트 내역', icon: '📊' },
  { id: 'ip', label: 'IP 관리', icon: '🛡️' },
];

// 모바일 고객 상세 페이지 컴포넌트
const CustomerDetailMobile: React.FC = () => {
  // URL 파라미터에서 userIdx 가져오기
  const { userIdx } = useParams<{ userIdx: string }>();
  const navigate = useNavigate();

  // 상태 관리
  const [customerDetail, setCustomerDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('payments');
  const [editName, setEditName] = useState('');
  const [editBusinessLicense, setEditBusinessLicense] = useState('');
  const [editCorporateLicense, setEditCorporateLicense] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileFlash, setProfileFlash] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // 고객 상세 정보 로드
  const loadCustomerDetail = useCallback(async () => {
    if (!userIdx) {
      setError('고객 ID가 유효하지 않습니다.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 실제 API 호출 시도, 실패하면 목업 데이터 사용
      let detail: CustomerDetail;
      try {
        detail = await CustomerService.getCustomerDetail(parseInt(userIdx));
        console.log('실제 API 데이터 로드 성공:', detail);
      } catch (apiError) {
        setError('고객 상세 정보를 불러오는데 실패했습니다.');
        console.error('Customer detail loading error:', apiError);
        return;
      }

      setCustomerDetail(detail);
    } catch (err) {
      setError('고객 상세 정보를 불러오는데 실패했습니다.');
      console.error('Customer detail loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [userIdx]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadCustomerDetail();
  }, [loadCustomerDetail]);

  useEffect(() => {
    if (!customerDetail) return;
    setEditName(customerDetail.이름 ?? '');
    setEditBusinessLicense(customerDetail.사업자등록증 ?? '');
    setEditCorporateLicense(customerDetail.법인등록증 ?? '');
  }, [customerDetail]);

  const handleSaveProfile = async () => {
    if (!userIdx || !customerDetail) return;
    setProfileFlash(null);
    setProfileSaving(true);
    try {
      const updated = await CustomerService.patchCustomerProfile(parseInt(userIdx, 10), {
        이름: editName.trim(),
        사업자등록증: editBusinessLicense.trim() || null,
        법인등록증: editCorporateLicense.trim() || null,
      });
      setCustomerDetail(updated);
      setProfileFlash({ type: 'ok', text: '저장되었습니다.' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '저장에 실패했습니다.';
      setProfileFlash({ type: 'err', text: msg });
    } finally {
      setProfileSaving(false);
    }
  };

  // 뒤로가기 핸들러
  const handleGoBack = () => {
    navigate('/mobile/customers');
  };

  // 날짜 포맷팅 (KST 적용)
  const formatDateTime = (dateTimeString: string) => {
    return formatDateTimeKST(dateTimeString);
  };

  // 숫자 포맷팅
  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  // 포인트 포맷팅
  const formatPoints = (points: number) => {
    return `${formatNumber(points)} P`;
  };

  // 금액 포맷팅
  const formatCurrency = (amount: number) => {
    return `${formatNumber(amount)}원`;
  };

  // 탭 콘텐츠 렌더링
  const renderTabContent = () => {
    switch (activeTab) {
      case 'payments':
        return <PaymentHistoryTableMobile userIdx={parseInt(userIdx || '0')} />;
      case 'keywords':
        return <KeywordSettingTableMobile userIdx={parseInt(userIdx || '0')} />;
      case 'points':
        return <PointHistoryTableMobile userIdx={parseInt(userIdx || '0')} onPointUpdate={loadCustomerDetail} />;
      case 'ip':
        return <CustomerIpAllowlistTab userIdx={parseInt(userIdx || '0')} />;
      default:
        return null;
    }
  };

  // 로딩 상태 처리
  if (loading) {
    return (
      <MobilePageContainer>
        <MobileHeader>
          <MobileHeaderContent>
            <MobileBackButton onClick={handleGoBack}>
              <FiArrowLeft size={20} />
            </MobileBackButton>
            <MobileHeaderTitle>고객 상세 정보</MobileHeaderTitle>
          </MobileHeaderContent>
        </MobileHeader>
        <MobileLoadingContainer>
          <div className="spinner" />
        </MobileLoadingContainer>
      </MobilePageContainer>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <MobilePageContainer>
        <MobileHeader>
          <MobileHeaderContent>
            <MobileBackButton onClick={handleGoBack}>
              <FiArrowLeft size={20} />
            </MobileBackButton>
            <MobileHeaderTitle>고객 상세 정보</MobileHeaderTitle>
          </MobileHeaderContent>
        </MobileHeader>
        <MobileErrorMessage>{error}</MobileErrorMessage>
      </MobilePageContainer>
    );
  }

  // 데이터가 없는 경우 처리
  if (!customerDetail) {
    return (
      <MobilePageContainer>
        <MobileHeader>
          <MobileHeaderContent>
            <MobileBackButton onClick={handleGoBack}>
              <FiArrowLeft size={20} />
            </MobileBackButton>
            <MobileHeaderTitle>고객 상세 정보</MobileHeaderTitle>
          </MobileHeaderContent>
        </MobileHeader>
        <MobileErrorMessage>고객 정보를 찾을 수 없습니다.</MobileErrorMessage>
      </MobilePageContainer>
    );
  }

  return (
    <MobilePageContainer>
      {/* 모바일 헤더 */}
      <MobileHeader>
        <MobileHeaderContent>
          <MobileBackButton onClick={handleGoBack}>
            <FiArrowLeft size={20} />
          </MobileBackButton>
          <MobileHeaderTitle>고객 상세 정보</MobileHeaderTitle>
        </MobileHeaderContent>
      </MobileHeader>

      {/* 모바일 콘텐츠 */}
      <MobileContent>
        {/* 프로필 카드 */}
        <MobileProfileCard>
          <MobileAvatar />
          <MobileCustomerId>idx: {customerDetail.user_idx} | ID: {customerDetail.아이디}</MobileCustomerId>

          {/* 통계 그리드 */}
          <MobileStatsGrid>
            <MobileStatsCard>
              <div className="value">{formatPoints(customerDetail.잔여포인트)}</div>
              <div className="label">잔여 포인트</div>
            </MobileStatsCard>
            <MobileStatsCard>
              <div className="value">{formatCurrency(customerDetail.총결제금액)}</div>
              <div className="label">총 결제금액</div>
            </MobileStatsCard>
            <MobileStatsCard>
              <div className="value">{formatNumber(customerDetail.활성키워드수)}건</div>
              <div className="label">활성 키워드</div>
            </MobileStatsCard>
            <MobileStatsCard>
              <div className="value">{formatNumber(customerDetail.전일메시지수)}건</div>
              <div className="label">전일 메시지</div>
            </MobileStatsCard>
          </MobileStatsGrid>
        </MobileProfileCard>

        {/* 기본 정보 카드 */}
        <MobileInfoCard>
          <MobileCardTitle>기본 정보</MobileCardTitle>
          <MobileColumnRow>
            <MobileInfoLabel>
              <FiUser className="icon" />
              이름
            </MobileInfoLabel>
            <MobileTextInput
              type="text"
              maxLength={20}
              value={editName}
              onChange={e => setEditName(e.target.value)}
              placeholder="이름 또는 회사명"
              aria-label="이름"
            />
          </MobileColumnRow>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiUser className="icon" />
              사용자 유형
            </MobileInfoLabel>
            <MobileInfoValue>{customerDetail.사용자유형}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiMail className="icon" />
              이메일
            </MobileInfoLabel>
            <MobileInfoValue>{customerDetail.이메일}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiPhone className="icon" />
              연락처
            </MobileInfoLabel>
            <MobileInfoValue>{customerDetail.연락처}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>마케팅 동의</MobileInfoLabel>
            <MobileInfoValue>{customerDetail.마케팅동의 ? '동의' : '미동의'}</MobileInfoValue>
          </MobileInfoRow>
        </MobileInfoCard>

        {/* 가입 및 로그인 정보 카드 */}
        <MobileInfoCard>
          <MobileCardTitle>가입 및 로그인 정보</MobileCardTitle>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiCalendar className="icon" />
              가입일
            </MobileInfoLabel>
            <MobileInfoValue>{formatDateTime(customerDetail.가입일)}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiCalendar className="icon" />
              최근 로그인
            </MobileInfoLabel>
            <MobileInfoValue>{formatDateTime(customerDetail.최근로그인)}</MobileInfoValue>
          </MobileInfoRow>
        </MobileInfoCard>

        {/* 결제 및 포인트 정보 카드 */}
        <MobileInfoCard>
          <MobileCardTitle>결제 및 포인트 정보</MobileCardTitle>
          <MobileInfoRow>
            <MobileInfoLabel>
              <FiCreditCard className="icon" />
              한달내 결제금액
            </MobileInfoLabel>
            <MobileInfoValue>{formatCurrency(customerDetail.한달내결제금액)}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>전일 사용포인트</MobileInfoLabel>
            <MobileInfoValue>{formatPoints(customerDetail.전일사용포인트)}</MobileInfoValue>
          </MobileInfoRow>
          <MobileInfoRow>
            <MobileInfoLabel>비활성 키워드</MobileInfoLabel>
            <MobileInfoValue>{formatNumber(customerDetail.비활성키워드수)}건</MobileInfoValue>
          </MobileInfoRow>
        </MobileInfoCard>

        <MobileInfoCard>
          <MobileCardTitle>사업자 정보</MobileCardTitle>
          <MobileColumnRow>
            <MobileInfoLabel>사업자 등록증</MobileInfoLabel>
            <MobileTextInput
              type="text"
              maxLength={12}
              value={editBusinessLicense}
              onChange={e => setEditBusinessLicense(e.target.value)}
              placeholder="없으면 비워 두세요"
              aria-label="사업자등록번호"
            />
          </MobileColumnRow>
          <MobileColumnRow>
            <MobileInfoLabel>법인 등록증</MobileInfoLabel>
            <MobileTextInput
              type="text"
              maxLength={14}
              value={editCorporateLicense}
              onChange={e => setEditCorporateLicense(e.target.value)}
              placeholder="없으면 비워 두세요"
              aria-label="법인등록번호"
            />
          </MobileColumnRow>
          <MobileSaveButton
            type="button"
            onClick={handleSaveProfile}
            disabled={profileSaving || !editName.trim()}
          >
            {profileSaving ? '저장 중…' : '이름·등록번호 저장'}
          </MobileSaveButton>
          {profileFlash ? (
            <MobileProfileFlash $error={profileFlash.type === 'err'}>{profileFlash.text}</MobileProfileFlash>
          ) : null}
        </MobileInfoCard>

        {/* 모바일 탭 섹션 */}
        <MobileTabContainer>
          <MobileTabHeader>
            {tabs.map((tab) => (
              <MobileTabButton
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <MobileTabIcon>{tab.icon}</MobileTabIcon>
                {tab.label}
              </MobileTabButton>
            ))}
          </MobileTabHeader>
          <MobileTabContent>
            {renderTabContent()}
          </MobileTabContent>
        </MobileTabContainer>
      </MobileContent>
    </MobilePageContainer>
  );
};

export default CustomerDetailMobile; 